// src/ui/features/calculator/hooks/useCalculatorEngine.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  analyse as analyseApi,
  calculate as calculateApi,
  markCalculation,
} from "../../../firebase/functions";

import {
  mapEngineResultToUi,
  normalizeNumber,
  roundDecimal,
  toEngineInputs,
} from "../utils/calculatorUtils";

function creditsExactForRuleset(nextModulesByLevel, levels) {
  let allExact = true;
  let anyBelow = false;

  for (const lvl of levels || []) {
    const required = Number(lvl.credits) || 0;
    if (required <= 0) continue;

    const mods = nextModulesByLevel?.[lvl.key] || [];
    const total = mods.reduce((s, m) => s + normalizeNumber(m?.credits, 0), 0);

    if (total > required) return [false, false];

    if (total < required) {
      allExact = false;
      anyBelow = true;
    }
  }

  const shouldAnalyse = anyBelow && !allExact;
  return [allExact, shouldAnalyse];
}

function validateForAnalyseWith(modulesByLevel, levels) {
  for (const lvl of levels || []) {
    const required = Number(lvl.credits) || 0;
    const mods = modulesByLevel?.[lvl.key] || [];
    const total = mods.reduce((s, m) => s + normalizeNumber(m?.credits, 0), 0);

    // Analyse only blocks when OVER
    if (required > 0 && total > required) {
      return `Credits exceed required: ${lvl.key.toUpperCase()} must total ${required} credits (currently ${total}).`;
    }
  }
  return "";
}

export function useCalculatorEngine({
  university,
  degreeType,
  ruleset,
  modulesByLevel,
  setModulesByLevel,
  targetGrade,
  incrementCalculations,
  canUseProFeatures,
  proLockedMessage,

  // free limit inputs
  isPro,
  freeCalcCount = 0,
  freeCalcLimit = 5,
  onRequireUpgrade,
}) {
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [calculateLoading, setCalculateLoading] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const [analysis, setAnalysis] = useState(null);
  const [analysisError, setAnalysisError] = useState("");

  // refs for analyse
  const analyseReqIdRef = useRef(0);
  const analyseDebounceRef = useRef(null);
  const lastAnalysePayloadKeyRef = useRef("");

  const latestModulesRef = useRef(modulesByLevel);
  const latestTargetRef = useRef(targetGrade);

  // refs for auto-calc
  const autoCalcDebounceRef = useRef(null);
  const lastAutoCalcPayloadKeyRef = useRef("");

  const freeCalcCountSafe = useMemo(() => {
    const n = Number.parseInt(String(freeCalcCount ?? "0"), 10);
    return Number.isFinite(n) ? n : 0;
  }, [freeCalcCount]);

  const freeCalcLimitSafe = useMemo(() => {
    const n = Number.parseInt(String(freeCalcLimit ?? "5"), 10);
    return Number.isFinite(n) && n > 0 ? n : 5;
  }, [freeCalcLimit]);

  // only “reached” when limit is a positive number and count >= limit
  const freeLimitReached = useMemo(() => {
    return !isPro && freeCalcLimitSafe > 0 && freeCalcCountSafe >= freeCalcLimitSafe;
  }, [isPro, freeCalcLimitSafe, freeCalcCountSafe]);

  // Guard against mobile double-fire (touchend + click / fast double tap)
  const actionLockRef = useRef({ addAt: 0, removeAt: 0 });
  const ACTION_LOCK_MS = 250;

  // derived levels/min grades
  const levels = useMemo(() => ruleset?.structure?.levels || [], [ruleset]);
  const minGrade = useMemo(() => ruleset?.simulation?.minGrade ?? 30, [ruleset]);
  const minDegreeGrade = useMemo(() => ruleset?.minDegreeGrade ?? 40, [ruleset]);

  const requiredCreditsByLevel = useMemo(() => {
    const out = {};
    for (const lvl of levels) out[lvl.key] = Number(lvl.credits) || 0;
    return out;
  }, [levels]);

  const totals = useMemo(() => {
    const out = {};
    for (const lvl of levels) {
      const arr = modulesByLevel?.[lvl.key] || [];
      out[lvl.key] = arr.reduce((s, m) => s + normalizeNumber(m?.credits, 0), 0);
    }
    return out;
  }, [levels, modulesByLevel]);

  const remainingCreditsByLevel = useMemo(() => {
    const out = {};
    for (const lvl of levels) {
      const required = requiredCreditsByLevel[lvl.key] || 0;
      const total = totals[lvl.key] || 0;
      out[lvl.key] = Math.max(0, required - total);
    }
    return out;
  }, [levels, requiredCreditsByLevel, totals]);

  const totalRemainingCredits = useMemo(() => {
    return Object.values(remainingCreditsByLevel).reduce(
      (s, v) => s + (Number(v) || 0),
      0
    );
  }, [remainingCreditsByLevel]);

  const validate = useCallback(() => {
    if (!ruleset) return "Ruleset not loaded.";

    for (const lvl of levels) {
      const total = totals[lvl.key] || 0;
      const required = requiredCreditsByLevel[lvl.key] || 0;
      if (required > 0 && total !== required) {
        return `Credits mismatch: ${lvl.key.toUpperCase()} must total ${required} credits (currently ${total}).`;
      }
    }
    return "";
  }, [ruleset, levels, totals, requiredCreditsByLevel]);

  const creditsValidationMessage = useMemo(() => validate(), [validate]);
  const creditsOk = !creditsValidationMessage;

  const calculateDisabled = !ruleset || !university?.id || !degreeType || !creditsOk;

  // module stats for charts + export breakdown
  const moduleStats = useMemo(() => {
    const all = [];
    for (const lvl of levels) {
      const levelKey = lvl.key;
      const mods = modulesByLevel?.[levelKey] || [];
      mods.forEach((m, i) => {
        const grade = Number(m?.grade);
        all.push({
          key: `${levelKey}-${i}`,
          levelKey,
          moduleIndex: i,
          label: `${levelKey.toUpperCase()} • M${i + 1}`,
          grade: Number.isFinite(grade) ? grade : null,
        });
      });
    }

    const graded = all.filter((x) => x.grade != null);
    const best = graded.length
      ? graded.reduce((a, b) => (b.grade > a.grade ? b : a))
      : null;
    const worst = graded.length
      ? graded.reduce((a, b) => (b.grade < a.grade ? b : a))
      : null;

    return { modules: all, best, worst };
  }, [levels, modulesByLevel]);

  const targetHelperText = useMemo(() => {
    if (!canUseProFeatures)
      return proLockedMessage || "Upgrade to Pro to unlock analysis.";
    if (totalRemainingCredits <= 0)
      return "No remaining credits detected (all years match required credits).";
    return "Release the slider to run analysis against your current inputs.";
  }, [canUseProFeatures, proLockedMessage, totalRemainingCredits]);

  // keep refs in sync
  useEffect(() => {
    latestModulesRef.current = modulesByLevel;
  }, [modulesByLevel]);

  useEffect(() => {
    latestTargetRef.current = targetGrade;
  }, [targetGrade]);

  const cancelAnalyse = useCallback(() => {
    if (analyseDebounceRef.current) clearTimeout(analyseDebounceRef.current);
    analyseDebounceRef.current = null;
    setAnalysisLoading(false);
  }, []);

  // if pro is lost, clear analysis (and cancel)
  useEffect(() => {
    if (!canUseProFeatures) {
      cancelAnalyse();
      setAnalysis(null);
      setAnalysisError("");
      lastAnalysePayloadKeyRef.current = "";
    }
  }, [canUseProFeatures, cancelAnalyse]);

  // schedule analyse (debounced + dedupe)
  const scheduleAnalyse = useCallback(
    (targetOverride, modulesOverride) => {
      if (!ruleset || !university?.id || !degreeType) return;

      if (!canUseProFeatures) {
        cancelAnalyse();
        setAnalysis(null);
        return;
      }

      if (analyseDebounceRef.current) clearTimeout(analyseDebounceRef.current);

      analyseDebounceRef.current = setTimeout(async () => {
        const modulesForAnalyse = modulesOverride ?? latestModulesRef.current;

        const v = validateForAnalyseWith(modulesForAnalyse, ruleset?.structure?.levels || []);
        if (v) {
          setAnalysis(null);
          setAnalysisError(v);
          setAnalysisLoading(false);
          return;
        }

        const inputs = toEngineInputs(modulesForAnalyse);
        const target =
          typeof targetOverride === "number"
            ? targetOverride
            : latestTargetRef.current;

        const payloadKey = JSON.stringify({
          universityId: university.id,
          degreeType,
          inputs,
          targetGrade: target,
        });

        if (payloadKey === lastAnalysePayloadKeyRef.current) return;
        lastAnalysePayloadKeyRef.current = payloadKey;

        const requestId = ++analyseReqIdRef.current;
        setAnalysisError("");
        setAnalysisLoading(true);

        try {
          const resp = await analyseApi({
            universityId: university.id,
            degreeType,
            inputs,
            targetGrade: target,
          });

          if (requestId !== analyseReqIdRef.current) return;
          setAnalysis(resp?.analysis ?? null);
        } catch (e) {
          if (requestId !== analyseReqIdRef.current) return;
          setAnalysis(null);
          setAnalysisError(e?.message || "Failed to analyse.");
        } finally {
          if (requestId === analyseReqIdRef.current) setAnalysisLoading(false);
        }
      }, 350);
    },
    [ruleset, university?.id, degreeType, canUseProFeatures, cancelAnalyse]
  );

  const clampOnBlur = useCallback(
    (levelKey, idx, field) => {
      setModulesByLevel((prev) => {
        const next = [...(prev[levelKey] || [])];
        const curr = next[idx]?.[field];

        if (field === "credits") {
          const c = normalizeNumber(curr, 15);
          next[idx].credits = Math.max(0, Math.min(180, c));
        }

        if (field === "grade") {
          const g = normalizeNumber(curr, minGrade);
          const clamped = Math.max(minGrade, Math.min(100, g));
          next[idx].grade = roundDecimal(clamped, 2);
        }

        return { ...prev, [levelKey]: next };
      });
    },
    [setModulesByLevel, minGrade]
  );

  const clampAndAnalyse = useCallback(
    (levelKey, idx, field) => {
      clampOnBlur(levelKey, idx, field);
      scheduleAnalyse();
    },
    [clampOnBlur, scheduleAnalyse]
  );

  const addModule = useCallback(
    (levelKey) => {
      const now = Date.now();
      if (now - actionLockRef.current.addAt < ACTION_LOCK_MS) return;
      actionLockRef.current.addAt = now;

      setModulesByLevel((prev) => {
        const nextLevel = [...(prev[levelKey] || []), { credits: 15, grade: minGrade }];
        const next = { ...prev, [levelKey]: nextLevel };
        latestModulesRef.current = next;

        const [allExact, shouldAnalyse] = creditsExactForRuleset(
          next,
          ruleset?.structure?.levels || []
        );
        const isAbove = !allExact && !shouldAnalyse;

        if (isAbove) {
          cancelAnalyse();
          setAnalysis(null);
          setAnalysisError("");
          setResult(null);
          setError("");
        } else if (shouldAnalyse) {
          setResult(null);
          scheduleAnalyse(undefined, next);
        } else if (allExact) {
          cancelAnalyse();
          setAnalysis(null);
          setAnalysisError("");
          lastAutoCalcPayloadKeyRef.current = "";
        }

        return next;
      });
    },
    [setModulesByLevel, minGrade, ruleset, cancelAnalyse, scheduleAnalyse]
  );

  const updateModule = useCallback(
    (levelKey, idx, field, value) => {
      setModulesByLevel((prev) => {
        const nextLevel = [...(prev[levelKey] || [])];

        let v = value;
        if (field === "credits") v = normalizeNumber(value, 0);
        if (field === "grade") v = value;

        nextLevel[idx] = { ...nextLevel[idx], [field]: v };
        const next = { ...prev, [levelKey]: nextLevel };
        latestModulesRef.current = next;
        return next;
      });
    },
    [setModulesByLevel]
  );

  const removeModule = useCallback(
    (levelKey, idx) => {
      const now = Date.now();
      if (now - actionLockRef.current.removeAt < ACTION_LOCK_MS) return;
      actionLockRef.current.removeAt = now;

      setModulesByLevel((prev) => {
        const nextLevel = (prev[levelKey] || []).filter((_, i) => i !== idx);
        const next = { ...prev, [levelKey]: nextLevel };
        latestModulesRef.current = next;

        const [allExact, shouldAnalyse] = creditsExactForRuleset(
          next,
          ruleset?.structure?.levels || []
        );
        const isAbove = !allExact && !shouldAnalyse;

        if (isAbove) {
          cancelAnalyse();
          setAnalysis(null);
          setAnalysisError("");
          setResult(null);
          setError("");
        } else if (shouldAnalyse) {
          setResult(null);
          scheduleAnalyse(undefined, next);
        } else if (allExact) {
          cancelAnalyse();
          setAnalysis(null);
          setAnalysisError("");
          lastAutoCalcPayloadKeyRef.current = "";
        }

        return next;
      });
    },
    [setModulesByLevel, ruleset, cancelAnalyse, scheduleAnalyse]
  );

  const runCalculate = useCallback(
    async ({ source = "manual" } = {}) => {
      setError("");

      // ✅ Free users: block ALL auto calculations (and do NOT count)
      if (!isPro && source === "auto") return;

      // ✅ Free users: only manual counts, and limited
      if (!isPro && source === "manual" && freeLimitReached) {
        onRequireUpgrade?.();
        return;
      }

      const v = validate();
      if (v) {
        setError(v);
        return;
      }

      if (!isPro && source === "manual") {
        await incrementCalculations?.({ countFree: true }); // <-- THIS increments local freeCalcCount
        markCalculation(); // your existing global tracking
      }

      setCalculateLoading(true);

      try {
        const inputs = toEngineInputs(modulesByLevel);

        const resp = await calculateApi({
          universityId: university.id,
          degreeType,
          inputs,
        });

        const uiResult = mapEngineResultToUi(resp?.result);
        setResult(uiResult);

        if (uiResult?.analysis != null) {
          setAnalysis(uiResult.analysis);
          setAnalysisError("");
        }
      } catch (e) {
        setError(e?.message || "Failed to calculate.");
      } finally {
        setCalculateLoading(false);
      }
    },
    [
      isPro,
      freeLimitReached,
      onRequireUpgrade,
      validate,
      incrementCalculations,
      modulesByLevel,
      university?.id,
      degreeType,
    ]
  );

  const onCalculate = useCallback(
    () => runCalculate({ source: "manual" }),
    [runCalculate]
  );

  const onAnalyseCommit = useCallback(
    (nextTargetGrade) => {
      scheduleAnalyse(nextTargetGrade);
    },
    [scheduleAnalyse]
  );

  // auto-calc (PRO ONLY)
  useEffect(() => {
    if (!ruleset || !university?.id || !degreeType) return;
    if (!creditsOk) return;

    // ✅ Free users cannot run auto-calc
    if (!isPro) return;

    if (autoCalcDebounceRef.current) clearTimeout(autoCalcDebounceRef.current);

    autoCalcDebounceRef.current = setTimeout(() => {
      const inputs = toEngineInputs(modulesByLevel);
      const payloadKey = JSON.stringify({
        universityId: university.id,
        degreeType,
        inputs,
      });

      if (payloadKey === lastAutoCalcPayloadKeyRef.current) return;
      lastAutoCalcPayloadKeyRef.current = payloadKey;

      runCalculate({ source: "auto" });
    }, 350);

    return () => {
      if (autoCalcDebounceRef.current) clearTimeout(autoCalcDebounceRef.current);
    };
  }, [
    creditsOk,
    ruleset,
    university?.id,
    degreeType,
    modulesByLevel,
    runCalculate,
    isPro,
  ]);

  return {
    // derived
    levels,
    minGrade,
    minDegreeGrade,
    requiredCreditsByLevel,
    totals,
    remainingCreditsByLevel,
    totalRemainingCredits,

    // validation
    creditsOk,
    creditsValidationMessage,
    calculateDisabled,

    // results
    result,
    error,
    analysis,
    analysisError,

    // UI copy
    targetHelperText,

    // actions
    addModule,
    updateModule,
    clampAndAnalyse,
    removeModule,
    onCalculate,
    onAnalyseCommit,

    // stats
    moduleStats,
    calculateLoading,
    analysisLoading,

    // free gate
    freeLimitReached,
    freeCalcLimit: freeCalcLimitSafe,
    freeCalcCount: freeCalcCountSafe,
  };
}